import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Alert, AlertTitle, Box, Button, CircularProgress, List, ListItem, Typography } from '@mui/material'

import { FailureDetail, FailureReason } from '../../../api/models'
import styles from '../MicroLearningDetail.module.css'

const REASON_LABELS: Record<FailureReason, string> = {
  missing_result_deck: 'Missing result deck',
  rag_not_ready: 'Content not ready (RAG)',
  insufficient_content: 'Insufficient content',
}

const reasonLabel = (reason: FailureReason | string): string =>
  (REASON_LABELS as Record<string, string>)[reason] ?? reason

type ReprocessWarningProps = {
  failureDetails?: FailureDetail[]
  canReprocess: boolean
  isReprocessing: boolean
  onReprocess: () => void
}

const ReprocessWarning = ({ failureDetails, canReprocess, isReprocessing, onReprocess }: ReprocessWarningProps) => {
  const hasDetails = Boolean(failureDetails?.length)

  return (
    <Alert severity='warning' icon={<WarningAmberIcon />} className={styles.warningBanner}>
      <AlertTitle>This micro-learning could not be processed.</AlertTitle>

      {hasDetails ? (
        <List dense disablePadding>
          {failureDetails?.map((detail, index) => (
            <ListItem key={`${detail.experiment_code}-${index}`} disableGutters sx={{ display: 'block', py: 0.5 }}>
              <Typography component='span' variant='body2' fontWeight={600}>
                {detail.experiment_code}
                {detail.reason ? ` — ${reasonLabel(detail.reason)}` : ''}
              </Typography>
              {detail.details ? (
                <Typography component='p' variant='body2'>
                  {detail.details}
                </Typography>
              ) : null}
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant='body2'>No structured failure details are available for this micro-learning.</Typography>
      )}

      {canReprocess ? (
        <Box sx={{ mt: 1.5 }}>
          <Button
            variant='contained'
            color='warning'
            size='small'
            disabled={isReprocessing}
            onClick={onReprocess}
            aria-label='Reprocess micro-learning'
            startIcon={isReprocessing ? <CircularProgress size={16} color='inherit' /> : undefined}
          >
            {isReprocessing ? 'Reprocessing…' : 'Reprocess'}
          </Button>
        </Box>
      ) : null}
    </Alert>
  )
}

export default ReprocessWarning
